/**
 * Helper for stage agents writing custom sounds with `audio.register()`:
 *
 *   import { kitFor } from '@audio/kit';
 *   import { tone, noise, fm } from '@audio/sfx';
 *   ctx.audio.register('jelly_pulse', (ac, out, opts) => {
 *     const k = kitFor(ac, out, opts);
 *     tone(k, { f: 300, to: 600, dur: 0.3, type: 'sine', rev: 0.5 });
 *     noise(k, { color: 'pink', dur: 0.2, bp: 900, q: 2, vol: 0.2 });
 *   });
 *
 * `out` is already volume-scaled/spatialised by the engine; `rev` sends go to the shared
 * SFX reverb.
 */
import type { SfxOptions } from '@core/services';
import { makeNoiseBuffer, type NoiseColor } from './dsp';
import type { Kit } from './sfx';

const reverbs = new WeakMap<BaseAudioContext, AudioNode>();
const noises = new WeakMap<BaseAudioContext, Map<NoiseColor, AudioBuffer>>();

/** Called by the engine so custom sounds can use the shared reverb. */
export function setSharedReverb(ctx: BaseAudioContext, node: AudioNode): void {
  reverbs.set(ctx, node);
}

export function noiseFor(ctx: BaseAudioContext, color: NoiseColor): AudioBuffer {
  let m = noises.get(ctx);
  if (!m) noises.set(ctx, (m = new Map()));
  let b = m.get(color);
  if (!b) m.set(color, (b = makeNoiseBuffer(ctx, 2, color)));
  return b;
}

/** Build a Kit for the sfx helpers. `opts.pitch` already includes the engine's random variance. */
export function kitFor(ctx: AudioContext, out: AudioNode, opts: SfxOptions = {}): Kit {
  return {
    ctx,
    out,
    rev: reverbs.get(ctx) ?? out,
    t: ctx.currentTime + 0.005,
    p: opts.pitch ?? 1,
    v: 1,
    noise: (c) => noiseFor(ctx, c),
  };
}
